import { useEffect, useState, type MouseEvent } from "react";
import { useSearchParams } from "react-router";
import { useMap } from "react-map-gl/mapbox";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  ListItemButton,
  Menu,
  MenuItem,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  deleteParamValue,
  updateParamValue,
  usePagePrefs,
} from "@pepsico/pref-permissions";

import type {
  PlotGeoResponse,
  SiteGeoResponse,
  TrialGeoResponse,
} from "api/model";
import { useGetPlotsGeoByLocation } from "api/plots-geo/plots-geo";
import {
  SELECTED_MAP_ITEM_BGCOLOR,
  SELECTED_MAP_ITEM_BORDER_COLOR,
} from "components/PlotMap/utils/display";
import { fitMapToBoundary } from "components/PlotMap/utils/mapCameraUtils";
import { readSelectedTrialGeoId } from "components/PlotMap/utils/selectionPrefs";
import { getTrialGeoLabel } from "context/PlotMap/plotGridGeoUtils";
import { GROWER_PREF_KEY, SITE_PREF_KEY, TRIAL_PREF_KEY } from "defs";

interface PlotTrialPickerProps {
  isDisabled: boolean;
  sites: SiteGeoResponse[];
  trials: TrialGeoResponse[];
}

const trialBarSx: SxProps<Theme> = {
  bgcolor: "success.light",
  borderRadius: 0,
  justifyContent: "space-between",
  px: 2,
  "&:hover": { bgcolor: "success.light" },
  "&.Mui-disabled": { bgcolor: "success.light", opacity: 1 },
};

const trialMenuItemSx: SxProps<Theme> = {
  alignItems: "flex-start",
  flexDirection: "column",
};

const selectedTrialMenuItemSx: SxProps<Theme> = {
  ...trialMenuItemSx,
  bgcolor: SELECTED_MAP_ITEM_BGCOLOR,
  borderLeftColor: SELECTED_MAP_ITEM_BORDER_COLOR,
  borderLeftStyle: "solid",
  borderLeftWidth: 4,
  "&:hover": { bgcolor: SELECTED_MAP_ITEM_BGCOLOR },
  "&.Mui-selected": { bgcolor: SELECTED_MAP_ITEM_BGCOLOR },
};

const getSiteLabel = (site: SiteGeoResponse): string => {
  const label = site.site_label?.trim() || "Unlabelled Site Boundary";
  return `${label} (${site.year})`;
};

const PlotTrialPicker: React.FC<PlotTrialPickerProps> = ({
  isDisabled,
  sites,
  trials,
}) => {
  const { default: map } = useMap();
  const [prefs, isReady] = usePagePrefs();
  const [, setSearchParams] = useSearchParams();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = anchorEl != null;
  const locationUuid = (prefs.location as string | null | undefined) ?? "";
  const { data: plots = [] } = useGetPlotsGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): PlotGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );

  const trialIdsWithPlots = new Set(plots.map((plot) => plot.trial_id));
  const trialsWithPlots = trials
    .filter((trial) => trialIdsWithPlots.has(trial.id))
    .sort((trial, other) =>
      getTrialGeoLabel(trial, trial.id).localeCompare(
        getTrialGeoLabel(other, other.id),
      ),
    );
  const selectedTrialId = readSelectedTrialGeoId(
    prefs,
    trials.map((trial) => trial.id),
  );
  const selectedTrial =
    trials.find((trial) => trial.id === selectedTrialId) ?? null;
  const selectedLabel =
    selectedTrial == null
      ? "No trial selected"
      : getTrialGeoLabel(selectedTrial, selectedTrial.id);

  useEffect(() => {
    if (isDisabled) {
      setAnchorEl(null);
    }
  }, [isDisabled]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl((current) => (current == null ? event.currentTarget : null));
  };

  const handleSelectTrial = (trial: TrialGeoResponse) => {
    if (!isReady || isDisabled) {
      return;
    }

    const site = sites.find((item) => item.id === trial.site_id) ?? null;
    const growerId = site?.grower_id ?? null;
    setSearchParams(
      (prev) => {
        let next =
          growerId == null
            ? deleteParamValue(prev, GROWER_PREF_KEY, prefs)
            : updateParamValue(prev, GROWER_PREF_KEY, prefs, String(growerId));
        next = updateParamValue(
          next,
          SITE_PREF_KEY,
          prefs,
          String(trial.site_id),
        );
        next = updateParamValue(next, TRIAL_PREF_KEY, prefs, String(trial.id));
        return next;
      },
      { replace: true, state: { inPageNav: true } },
    );
    fitMapToBoundary(map, site?.boundary);
    setAnchorEl(null);
  };

  return (
    <>
      <ListItemButton
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Selected trial"
        disabled={isDisabled}
        onClick={handleToggle}
        sx={trialBarSx}
      >
        <Typography
          color={selectedTrial == null ? "text.secondary" : "text.primary"}
          noWrap
          variant="body1"
        >
          {selectedLabel}
        </Typography>
        {isOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        onClose={handleClose}
        open={isOpen}
        slotProps={{
          list: { "aria-label": "Trials with plots" },
          paper: { sx: { width: anchorEl?.clientWidth } },
        }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
      >
        {trialsWithPlots.length === 0 ? (
          <MenuItem disabled>No trials with plots at this location.</MenuItem>
        ) : (
          trialsWithPlots.map((trial) => {
            const isSelected = trial.id === selectedTrialId;
            const site =
              sites.find((item) => item.id === trial.site_id) ?? null;
            return (
              <MenuItem
                key={trial.id}
                aria-pressed={isSelected}
                onClick={() => {
                  handleSelectTrial(trial);
                }}
                selected={isSelected}
                sx={isSelected ? selectedTrialMenuItemSx : trialMenuItemSx}
              >
                <Typography noWrap variant="body1">
                  {getTrialGeoLabel(trial, trial.id)}
                </Typography>
                {site != null && (
                  <Typography color="text.secondary" noWrap variant="body2">
                    {getSiteLabel(site)}
                  </Typography>
                )}
              </MenuItem>
            );
          })
        )}
      </Menu>
    </>
  );
};

export default PlotTrialPicker;
